import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils.ts";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx";
import Picker, { PickerValue } from "@/components/WheelDatePicker/Picker.tsx";
import PickerColumn from "@/components/WheelDatePicker/PickerColumn.tsx";
import PickerItem from "@/components/WheelDatePicker/PickerItem.tsx";
import { useTranslation } from "@/i18n";

interface WheelDatePickerProps {
  className?: string;
  date?: Date;
  placeholder?: string;
  onDateChange?: (date: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
}

const DEFAULT_DATE = {
  year: 2000,
  month: 0,
  day: 15,
  daysInTheMonth: 31,
};

const WheelDatePicker = ({
  className,
  date,
  placeholder,
  onDateChange,
}: WheelDatePickerProps) => {
  const { t } = useTranslation();
  const months = t("datePicker.months", { returnObjects: true });
  const [daysInMonth, setDaysInMonth] = useState<number[]>(
    Array.from(
      {
        length: date
          ? new Date(+date.getFullYear(), date.getMonth() + 1, 0).getDate()
          : DEFAULT_DATE.daysInTheMonth,
      },
      (_, i) => i + 1,
    ),
  );
  const [open, setOpen] = useState(false);
  const [pickerValue, setPickerValue] = useState<PickerValue>({
    year: date ? date.getFullYear() : DEFAULT_DATE.year,
    month: date ? date.getMonth() : DEFAULT_DATE.month,
    day: date ? date.getFullYear() : DEFAULT_DATE.day,
  });

  // Generate years (current year - 100 to current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => currentYear - 100 + i);

  // Initialize with the provided date
  useEffect(() => {
    if (date && !isNaN(date.getTime())) {
      setPickerValue({
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
      });
    }
  }, [date]);

  const handleConfirm = () => {
    if (onDateChange) {
      const newDate = new Date(
        +pickerValue.year,
        +pickerValue.month,
        +pickerValue.day,
      );
      onDateChange(newDate);
    }
    setOpen(false);
  };

  const handlePickerChange = useCallback(
    (newValue: PickerValue, key: string) => {
      if (key === "day") {
        setPickerValue(newValue);
        return;
      }

      const { year, month } = newValue;
      const daysCount = new Date(+year, +month + 1, 0).getDate();
      setDaysInMonth(Array.from({ length: daysCount }, (_, i) => i + 1));
      const newDay =
        daysCount >= +newValue.day ? newValue.day : +newValue.day - 1;
      setPickerValue({ ...newValue, day: newDay });
    },
    [],
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTitle>{t("datePicker.selectBirthday")}</SheetTitle>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "bg-card w-full pl-3 text-left font-normal",
            !date && "text-muted-foreground",
            className,
          )}
        >
          <Calendar className="mr-2 h-4 w-4" />
          {date
            ? date.toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : (placeholder ?? t("datePicker.placeholder"))}
        </Button>
      </SheetTrigger>
      <SheetContent
        className="bg-card border-border w-full overflow-hidden rounded-xl border shadow-lg"
        side="bottom"
      >
        <Picker
          value={pickerValue}
          onChange={handlePickerChange}
          wheelMode="natural"
        >
          <PickerColumn name="year">
            {years.map((year) => (
              <PickerItem key={year} value={year}>
                {({ selected }) => (
                  <div
                    className={
                      selected
                        ? "font-semibold text-neutral-900"
                        : "text-neutral-400"
                    }
                  >
                    {year}
                  </div>
                )}
              </PickerItem>
            ))}
          </PickerColumn>
          <PickerColumn name="month">
            {months.map((month, index) => (
              <PickerItem key={month} value={index}>
                {({ selected }) => (
                  <div
                    className={
                      selected
                        ? "font-semibold text-neutral-900"
                        : "text-neutral-400"
                    }
                  >
                    {month}
                  </div>
                )}
              </PickerItem>
            ))}
          </PickerColumn>
          <PickerColumn name="day">
            {daysInMonth.map((day) => (
              <PickerItem key={day} value={day}>
                {({ selected }) => (
                  <div
                    className={
                      selected
                        ? "font-semibold text-neutral-900"
                        : "text-neutral-400"
                    }
                  >
                    {day}
                  </div>
                )}
              </PickerItem>
            ))}
          </PickerColumn>
        </Picker>

        <div className="border-border flex justify-end border-t p-4">
          <Button variant="default" size="sm" onClick={handleConfirm}>
            {t("common.confirm")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WheelDatePicker;
